// src/context/APP/NoRecentVisit/NoRecentVisitContext.js
import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useValidToken } from "../../../hooks/useValidToken";

import { getNoRecentVisitPatients } from "../../../utils/services/APP/NoRecentVisit/getNoRecentVisitPatients";
import { getNoRecentVisitByCip } from "../../../utils/services/APP/NoRecentVisit/getNoRecentVisitByCip";

export const NoRecentVisitContext = createContext();

function normalizeCip(cipId) {
  return String(cipId ?? "").trim();
}

function extractFirstRecord(payload) {
  if (!payload) return null;

  // array
  if (Array.isArray(payload)) return payload[0] ?? null;

  // {data:[...]}
  if (Array.isArray(payload.data)) return payload.data[0] ?? null;

  // object record directo
  if (typeof payload === "object") return payload;

  return null;
}

export const NoRecentVisitProvider = ({ children, enableSummary = true }) => {
  const { isValid, authLoading } = useValidToken();

  const canCallApi = useCallback(() => !authLoading && isValid, [authLoading, isValid]);

  // ===== Summary (lista) =====
  const [NoRecentVisitPatients, setNoRecentVisitPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(50);

  const [loading, setLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!canCallApi()) return;

    setLoading(true);
    setSummaryError(null);
    try {
      const res = await getNoRecentVisitPatients(page + 1, pageSize); // API 1-based
      const data = Array.isArray(res?.data) ? res.data : [];
      setNoRecentVisitPatients(data.map((item) => ({ ...item, id: item.cipId })));
      setTotal(Number(res?.total ?? 0));
    } catch (error) {
      console.error("[NoRecentVisitContext] Error loading summary:", error);
      setSummaryError(error);
      setNoRecentVisitPatients([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canCallApi, page, pageSize]);

  // Auto summary (solo si enableSummary)
  useEffect(() => {
    if (!enableSummary) return;
    if (!isValid || authLoading) return;
    fetchSummary();
  }, [enableSummary, isValid, authLoading, fetchSummary]);

  // ===== Details (por CIP) =====
  const detailsCacheRef = useRef(new Map()); // cip -> record|null

  const [selectedCip, setSelectedCip] = useState(null);
  const [NoRecentVisitDetails, setNoRecentVisitDetails] = useState(null); // guardamos el record (no array)
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  const fetchNoRecentVisitDetails = useCallback(
    async (cipId, opts = {}) => {
      if (!canCallApi()) return null;

      const cip = normalizeCip(cipId);
      if (!cip) return null;

      const force = Boolean(opts.force);

      // Cache hit
      if (!force && detailsCacheRef.current.has(cip)) {
        const cached = detailsCacheRef.current.get(cip);
        setSelectedCip(cip);
        setNoRecentVisitDetails(cached);
        setDetailsError(null);
        return cached;
      }

      setDetailsLoading(true);
      setDetailsError(null);

      try {
        const res = await getNoRecentVisitByCip(cip);
        const record = extractFirstRecord(res);

        detailsCacheRef.current.set(cip, record);
        setSelectedCip(cip);
        setNoRecentVisitDetails(record);

        return record;
      } catch (error) {
        console.error("[NoRecentVisitContext] Error loading details by CIP:", cip, error);
        setDetailsError(error);
        setSelectedCip(cip);
        setNoRecentVisitDetails(null);
        detailsCacheRef.current.set(cip, null);
        return null;
      } finally {
        setDetailsLoading(false);
      }
    },
    [canCallApi]
  );

  const clearDetails = useCallback(() => {
    setSelectedCip(null);
    setNoRecentVisitDetails(null);
    setDetailsError(null);
    setDetailsLoading(false);
  }, []);

  const clearCache = useCallback(() => {
    detailsCacheRef.current.clear();
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isValid) {
      // limpia todo
      setNoRecentVisitPatients([]);
      setTotal(0);
      setLoading(false);
      setSummaryError(null);
      clearDetails();
      clearCache();
    }
  }, [authLoading, isValid, clearDetails, clearCache]);

  return (
    <NoRecentVisitContext.Provider
      value={{
        // summary
        NoRecentVisitPatients,
        total,
        page,
        pageSize,
        loading,
        summaryError,
        setPage,
        setPageSize,
        fetchSummary,

        // details
        selectedCip,
        NoRecentVisitDetails, // ✅ record directo
        detailsLoading,
        detailsError,
        fetchNoRecentVisitDetails,
        clearDetails,
        clearCache,
      }}
    >
      {children}
    </NoRecentVisitContext.Provider>
  );
};
